import React, { useState, useMemo, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFire, faPaperPlane, faCheckCircle, faExclamationTriangle, faUserGroup, faEye,
} from "@fortawesome/free-solid-svg-icons";
import ModalHeader from "./ModalHeader";
import SplitDateInput from "./SplitDateInput";
import api from "../services/api";
import styles from "./FriendChallengeModal.module.css";
import { useBodyScrollLock } from "../hooks/useBodyScrollLock";

const MAX_INVITES = 20;

const GOAL_TYPE_OPTIONS = [
  { value: "points", label: "Pontos", metaLabel: "Meta de pontos" },
  { value: "workouts", label: "Treinos", metaLabel: "Meta de treinos" },
  { value: "minutes", label: "Minutos", metaLabel: "Meta de minutos" },
  { value: "streak_days", label: "Dias seguidos", metaLabel: "Meta de dias seguidos" },
];

function parseEmails(raw) {
  return Array.from(
    new Set(
      raw
        .split(/[\n,;]+/)
        .map((s) => s.trim().toLowerCase())
        .filter(Boolean)
    )
  );
}

function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

const emptyForm = {
  title: "",
  description: "",
  goal_type: "points",
  goal_points: "",
  start_date: "",
  end_date: "",
  reward: "",
  photo_required: false,
};

export default function FriendChallengeModal({ open, onClose, userId, creatorName, onCreated }) {
  useBodyScrollLock(open);

  const [form, setForm] = useState(emptyForm);
  const [emailsText, setEmailsText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null); // { joined_now, invited_by_email }

  useEffect(() => {
    if (open) {
      setForm(emptyForm);
      setEmailsText("");
      setError(null);
      setResult(null);
      setSubmitting(false);
    }
  }, [open]);

  const emails = useMemo(() => parseEmails(emailsText), [emailsText]);
  const invalidEmails = useMemo(() => emails.filter((e) => !isValidEmail(e)), [emails]);
  const validEmails = useMemo(() => emails.filter((e) => isValidEmail(e)), [emails]);
  const tooManyEmails = validEmails.length > MAX_INVITES;

  const goalMeta = GOAL_TYPE_OPTIONS.find((g) => g.value === form.goal_type) || GOAL_TYPE_OPTIONS[0];

  const canSubmit =
    form.title.trim() &&
    form.goal_points &&
    form.start_date &&
    form.end_date &&
    !tooManyEmails &&
    !submitting;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setError(null);
    setSubmitting(true);
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description.trim() || `${creatorName || "Um amigo"} te chamou pra um desafio!`,
        goal_points: Number(form.goal_points),
        goal_type: form.goal_type,
        start_date: form.start_date,
        end_date: form.end_date,
        reward: form.reward.trim() || undefined,
        is_public: false,
        photo_required: form.photo_required,
        invite_emails: validEmails,
      };
      const res = await api.post(`/users/${userId}/challenges`, payload);
      setResult(res.data);
      onCreated?.(res.data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "object" ? detail.message : detail || "Erro ao criar desafio.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className={styles.overlay} role="dialog" aria-modal="true">
      <div className={styles.modal}>
        <ModalHeader onClose={onClose} disabled={submitting} />

        <div className={styles.header}>
          <p className={styles.title}>
            <FontAwesomeIcon icon={faUserGroup} /> Desafio com amigos
          </p>
          <p className={styles.sub}>
            Crie um desafio privado e convide até {MAX_INVITES} pessoas por email.
            Só quem for convidado participa.
          </p>
        </div>

        <div className={styles.body}>
          {result ? (
            <div className={styles.resultBox}>
              <div className={styles.resultHeader}>
                <FontAwesomeIcon icon={faCheckCircle} className={styles.resultIcon} />
                <p className={styles.resultTitle}>Desafio criado!</p>
              </div>

              {result.joined_now?.length > 0 && (
                <div className={styles.resultSection}>
                  <p className={styles.resultLabel}>
                    {result.joined_now.length} amigo(s) já tinham conta e entraram direto:
                  </p>
                  <p className={styles.resultNames}>
                    {result.joined_now.map((u) => u.name).join(", ")}
                  </p>
                </div>
              )}

              {result.invited_by_email?.length > 0 && (
                <div className={styles.resultSection}>
                  <p className={styles.resultLabel}>
                    {result.invited_by_email.length} convite(s) por email enviado(s):
                  </p>
                  <p className={styles.resultNames}>{result.invited_by_email.join(", ")}</p>
                  <p className={styles.resultHint}>
                    Eles recebem um email explicando como criar conta e entrar no desafio automaticamente.
                  </p>
                </div>
              )}

              {!result.joined_now?.length && !result.invited_by_email?.length && (
                <p className={styles.resultHint}>Você é o único participante por enquanto.</p>
              )}

              <button type="button" className={styles.sendBtn} onClick={onClose}>
                Fechar
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className={styles.form}>
              <div className={styles.field}>
                <label htmlFor="fc-title" className={styles.label}>Título</label>
                <input
                  id="fc-title"
                  className={styles.input}
                  type="text"
                  placeholder="Ex.: Desafio para festa de fim de ano"
                  value={form.title}
                  onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                  maxLength={100}
                  required
                />
              </div>

              <div className={styles.field}>
                <label htmlFor="fc-description" className={styles.label}>Descrição (opcional)</label>
                <textarea
                  id="fc-description"
                  className={styles.textarea}
                  placeholder="Combine as regras com a galera aqui..."
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  maxLength={500}
                />
              </div>

              <div className={styles.row}>
                <div className={styles.field}>
                  <label htmlFor="fc-goal-type" className={styles.label}>Tipo de meta</label>
                  <select
                    id="fc-goal-type"
                    className={styles.input}
                    value={form.goal_type}
                    onChange={(e) => setForm((f) => ({ ...f, goal_type: e.target.value }))}
                  >
                    {GOAL_TYPE_OPTIONS.map((g) => (
                      <option key={g.value} value={g.value}>{g.label}</option>
                    ))}
                  </select>
                </div>
                <div className={styles.field}>
                  <label htmlFor="fc-goal-points" className={styles.label}>{goalMeta.metaLabel}</label>
                  <input
                    id="fc-goal-points"
                    className={styles.input}
                    type="number"
                    min="1"
                    placeholder="Ex.: 500"
                    value={form.goal_points}
                    onChange={(e) => setForm((f) => ({ ...f, goal_points: e.target.value }))}
                    required
                  />
                </div>
              </div>

              <div className={styles.row}>
                <div className={styles.field}>
                  <label htmlFor="fc-start" className={styles.label}>Início</label>
                  <SplitDateInput
                    value={form.start_date}
                    onChange={(v) => setForm((f) => ({ ...f, start_date: v }))}
                    inputClassName={styles.input}
                  />
                </div>
                <div className={styles.field}>
                  <label htmlFor="fc-end" className={styles.label}>Fim</label>
                  <SplitDateInput
                    value={form.end_date}
                    onChange={(v) => setForm((f) => ({ ...f, end_date: v }))}
                    inputClassName={styles.input}
                  />
                </div>
              </div>

              <div className={styles.field}>
                <label htmlFor="fc-reward" className={styles.label}>Recompensa (opcional)</label>
                <input
                  id="fc-reward"
                  className={styles.input}
                  type="text"
                  placeholder="Ex.: quem perder paga o rodízio"
                  value={form.reward}
                  onChange={(e) => setForm((f) => ({ ...f, reward: e.target.value }))}
                  maxLength={120}
                />
              </div>

              <label className={styles.checkboxRow}>
                <input
                  type="checkbox"
                  checked={form.photo_required}
                  onChange={(e) => setForm((f) => ({ ...f, photo_required: e.target.checked }))}
                />
                <span>
                  <strong>Exigir foto no check-in</strong>
                  <small>Cada registro de treino nesse desafio vai precisar de uma foto anexada, visível para todos os participantes.</small>
                </span>
              </label>

              <div className={styles.field}>
                <div className={styles.emailsHeader}>
                  <label htmlFor="fc-emails" className={styles.label}>Convidar por email</label>
                  <span className={`${styles.emailsCount} ${tooManyEmails ? styles.emailsCountOver : ""}`}>
                    {validEmails.length}/{MAX_INVITES}
                  </span>
                </div>
                <textarea
                  id="fc-emails"
                  className={styles.textarea}
                  placeholder="um email por linha (ou separados por vírgula)"
                  value={emailsText}
                  onChange={(e) => setEmailsText(e.target.value)}
                />
                <p className={styles.emailsHint}>
                  Quem já tem conta no TreinoNexo entra direto. Quem não tem, recebe um
                  convite por email - não expomos a lista de alunos, só quem você digitar aqui.
                </p>
                <p className={styles.privacyNote} role="note">
                  <FontAwesomeIcon icon={faEye} aria-hidden="true" />
                  <span>
                    Todos os participantes acompanham o desempenho do grupo e veem as fotos de check-in.
                  </span>
                </p>
                {tooManyEmails && (
                  <p className={styles.emailsError}>
                    Máximo de {MAX_INVITES} convites por desafio (você digitou {validEmails.length}).
                  </p>
                )}
                {invalidEmails.length > 0 && (
                  <p className={styles.emailsError}>
                    Email(s) com formato inválido, serão ignorados: {invalidEmails.join(", ")}
                  </p>
                )}
              </div>

              {error && (
                <div className={styles.feedbackError}>
                  <FontAwesomeIcon icon={faExclamationTriangle} /> {error}
                </div>
              )}

              <button type="submit" className={styles.sendBtn} disabled={!canSubmit}>
                <FontAwesomeIcon icon={submitting ? faFire : faPaperPlane} />
                {submitting ? "Criando..." : "Criar desafio"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
